import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Alert, Spinner, Badge, Pagination, InputGroup } from 'react-bootstrap';
import { stockAPI } from '../utils/api';

const StockMonitoring = () => {
  // Data states
  const [monitoringData, setMonitoringData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);

  // Filter states
  const [dateFrom, setDateFrom] = useState(getDefaultDateFrom());
  const [dateTo, setDateTo] = useState(getDefaultDateTo());
  const [category, setCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [outOfStockOnly, setOutOfStockOnly] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  // Expandable rows state
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Refs
  const searchDebounceRef = useRef(null);
  const hasLoadedRef = useRef(false);

  // Helper functions for default dates
  function getDefaultDateFrom() {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  }

  function getDefaultDateTo() {
    return new Date().toISOString().split('T')[0];
  }

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadCategories();
      loadMonitoringData();
    }
  }, []);

  useEffect(() => {
    if (hasLoadedRef.current) {
      loadMonitoringData();
    }
  }, [currentPage, category, outOfStockOnly, dateFrom, dateTo]);

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      if (hasLoadedRef.current) {
        setCurrentPage(1);
        loadMonitoringData();
      }
    }, 500);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchQuery]);

  const loadCategories = async () => {
    try {
      const res = await stockAPI.getCategories();
      setCategories(res.data.data);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  const loadMonitoringData = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page: currentPage,
        limit: itemsPerPage,
        dateFrom,
        dateTo
      };

      if (category) params.category = category;
      if (searchQuery) params.search = searchQuery;
      if (outOfStockOnly) params.outOfStockOnly = 'true';

      const res = await stockAPI.getMonitoring(params);
      setMonitoringData(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickFilter = (days) => {
    const today = new Date();
    const from = new Date();
    from.setDate(today.getDate() - days);

    setDateFrom(from.toISOString().split('T')[0]);
    setDateTo(today.toISOString().split('T')[0]);
  };

  const handleClearFilters = () => {
    setDateFrom(getDefaultDateFrom());
    setDateTo(getDefaultDateTo());
    setCategory('');
    setSearchQuery('');
    setOutOfStockOnly(false);
    setCurrentPage(1);
  };

  const toggleExpand = (materialId) => {
    const newSet = new Set(expandedRows);
    if (newSet.has(materialId)) {
      newSet.delete(materialId);
    } else {
      newSet.add(materialId);
    }
    setExpandedRows(newSet);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (material) => {
    if (material.isLowStock) {
      return <Badge bg="danger">สต็อกต่ำ</Badge>;
    } else if (material.availableQuantity <= 0) {
      return <Badge bg="warning" text="dark">หมด</Badge>;
    } else {
      return <Badge bg="success">ปกติ</Badge>;
    }
  };

  const renderPagination = () => {
    if (!monitoringData || monitoringData.pagination.totalPages <= 1) return null;

    const { currentPage, totalPages } = monitoringData.pagination;
    const pages = [];

    // First page
    pages.push(
      <Pagination.Item key={1} active={currentPage === 1} onClick={() => setCurrentPage(1)}>
        1
      </Pagination.Item>
    );

    // Ellipsis after first page
    if (currentPage > 3) {
      pages.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    }

    // Pages around current page
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      pages.push(
        <Pagination.Item key={i} active={currentPage === i} onClick={() => setCurrentPage(i)}>
          {i}
        </Pagination.Item>
      );
    }

    // Ellipsis before last page
    if (currentPage < totalPages - 2) {
      pages.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
    }

    // Last page
    if (totalPages > 1) {
      pages.push(
        <Pagination.Item
          key={totalPages}
          active={currentPage === totalPages}
          onClick={() => setCurrentPage(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    return (
      <div className="d-flex justify-content-between align-items-center p-3 border-top">
        <div className="text-muted">
          แสดง {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, monitoringData.pagination.totalCount)} จาก {monitoringData.pagination.totalCount} รายการ
        </div>
        <Pagination className="mb-0">
          <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
          <Pagination.Prev onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} />
          {pages}
          <Pagination.Next onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} />
          <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
        </Pagination>
      </div>
    );
  };

  return (
    <Container fluid className="main-content">
      <Row className="mb-4">
        <Col>
          <h2>
            <i className="bi bi-graph-up me-2"></i>
            ตรวจสอบสต็อก
          </h2>
          <p className="text-muted">ติดตามการเคลื่อนไหวและสถานะของสต็อกสินค้า</p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Summary Stats Cards */}
      {monitoringData && (
        <Row className="mb-4">
          <Col md={3}>
            <Card className="stats-card mb-3">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="text-muted small">วัตถุดิบทั้งหมด</div>
                    <h3 className="mb-0">{monitoringData.summary.totalMaterials}</h3>
                  </div>
                  <i className="bi bi-box-seam text-primary" style={{ fontSize: '2rem' }}></i>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stats-card mb-3">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="text-muted small">สต็อกต่ำ</div>
                    <h3 className="mb-0 text-danger">{monitoringData.summary.lowStockCount}</h3>
                  </div>
                  <i className="bi bi-exclamation-triangle text-danger" style={{ fontSize: '2rem' }}></i>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stats-card mb-3">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="text-muted small">มูลค่าสต็อกรวม</div>
                    <h3 className="mb-0">{formatCurrency(monitoringData.summary.totalInventoryValue)}</h3>
                  </div>
                  <i className="bi bi-cash-stack text-success" style={{ fontSize: '2rem' }}></i>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stats-card mb-3">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="text-muted small">เฉลี่ยสต็อก/รายการ</div>
                    <h3 className="mb-0">{monitoringData.summary.averageStockLevel.toFixed(2)}</h3>
                  </div>
                  <i className="bi bi-bar-chart text-info" style={{ fontSize: '2rem' }}></i>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Filter Panel */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">
            <i className="bi bi-funnel me-2"></i>
            ตัวกรอง
          </h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>วันที่เริ่มต้น</Form.Label>
                <Form.Control
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>วันที่สิ้นสุด</Form.Label>
                <Form.Control
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>หมวดหมู่</Form.Label>
                <Form.Select value={category} onChange={(e) => setCategory(e.target.value)}>
                  <option value="">ทั้งหมด</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>ค้นหา</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="ชื่อหรือรหัสวัตถุดิบ..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Check
                type="checkbox"
                label="แสดงเฉพาะสินค้าหมด"
                checked={outOfStockOnly}
                onChange={(e) => setOutOfStockOnly(e.target.checked)}
              />
            </Col>
            <Col md={6} className="text-end">
              <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => handleQuickFilter(0)}>
                วันนี้
              </Button>
              <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => handleQuickFilter(7)}>
                7 วัน
              </Button>
              <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => handleQuickFilter(30)}>
                30 วัน
              </Button>
              <Button variant="outline-primary" size="sm" onClick={handleClearFilters}>
                <i className="bi bi-x-circle me-1"></i>
                ล้างตัวกรอง
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Main Table */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">รายการสต็อก</h5>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">กำลังโหลดข้อมูล...</p>
            </div>
          ) : !monitoringData || monitoringData.materials.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-inbox display-1 text-muted"></i>
              <p className="text-muted mt-3">ไม่พบข้อมูล</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th>รหัส</th>
                  <th>ชื่อ</th>
                  <th>หมวดหมู่</th>
                  <th className="text-end">สต็อกปัจจุบัน</th>
                  <th className="text-end">จอง</th>
                  <th className="text-end">พร้อมใช้</th>
                  <th className="text-end">จุดสั่งใหม่</th>
                  <th className="text-end">มูลค่า</th>
                  <th className="text-center">สถานะ</th>
                </tr>
              </thead>
              <tbody>
                {monitoringData.materials.map((material) => (
                  <React.Fragment key={material.id}>
                    <tr
                      onClick={() => toggleExpand(material.id)}
                      style={{ cursor: 'pointer' }}
                      className={expandedRows.has(material.id) ? 'table-active' : ''}
                    >
                      <td>
                        <i className={`bi bi-chevron-${expandedRows.has(material.id) ? 'down' : 'right'}`}></i>
                      </td>
                      <td><strong>{material.code}</strong></td>
                      <td>{material.name}</td>
                      <td>{material.category || '-'}</td>
                      <td className="text-end">{material.currentQuantity.toFixed(2)} {material.unit}</td>
                      <td className="text-end">{material.quantityReserved.toFixed(2)} {material.unit}</td>
                      <td className="text-end"><strong>{material.availableQuantity.toFixed(2)} {material.unit}</strong></td>
                      <td className="text-end">{material.reorderPoint ? `${material.reorderPoint.toFixed(2)} ${material.unit}` : '-'}</td>
                      <td className="text-end">{formatCurrency(material.totalValue)}</td>
                      <td className="text-center">{getStatusBadge(material)}</td>
                    </tr>
                    {expandedRows.has(material.id) && (
                      <tr className="bg-light">
                        <td colSpan="10" style={{ padding: '20px' }}>
                          <Row>
                            <Col md={6}>
                              <h6 className="mb-3">สรุปการเคลื่อนไหว (ตลอดเวลา)</h6>
                              <Table size="sm" bordered>
                                <tbody>
                                  <tr>
                                    <td><strong>รับเข้าทั้งหมด:</strong></td>
                                    <td className="text-end text-success">
                                      <i className="bi bi-arrow-down-circle me-1"></i>
                                      +{material.transactions.totalIntake.toFixed(2)} {material.unit}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td><strong>เบิกออกทั้งหมด:</strong></td>
                                    <td className="text-end text-danger">
                                      <i className="bi bi-arrow-up-circle me-1"></i>
                                      -{material.transactions.totalOuttake.toFixed(2)} {material.unit}
                                    </td>
                                  </tr>
                                  <tr className="table-active">
                                    <td><strong>คงเหลือ:</strong></td>
                                    <td className="text-end"><strong>{material.currentQuantity.toFixed(2)} {material.unit}</strong></td>
                                  </tr>
                                  <tr>
                                    <td><strong>ธุรกรรมล่าสุด:</strong></td>
                                    <td className="text-end">{formatDate(material.transactions.lastTransaction)}</td>
                                  </tr>
                                </tbody>
                              </Table>
                            </Col>
                            <Col md={6}>
                              <h6 className="mb-3">การเคลื่อนไหวในช่วงที่เลือก ({dateFrom} ถึง {dateTo})</h6>
                              <Table size="sm" bordered>
                                <tbody>
                                  <tr>
                                    <td><strong>รับเข้าในช่วงนี้:</strong></td>
                                    <td className="text-end text-success">
                                      <i className="bi bi-arrow-down-circle me-1"></i>
                                      +{material.transactions.filteredIntake.toFixed(2)} {material.unit}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td><strong>เบิกออกในช่วงนี้:</strong></td>
                                    <td className="text-end text-danger">
                                      <i className="bi bi-arrow-up-circle me-1"></i>
                                      -{material.transactions.filteredOuttake.toFixed(2)} {material.unit}
                                    </td>
                                  </tr>
                                  <tr className="table-active">
                                    <td><strong>สุทธิในช่วงนี้:</strong></td>
                                    <td className="text-end">
                                      <strong>
                                        {(material.transactions.filteredIntake - material.transactions.filteredOuttake).toFixed(2)} {material.unit}
                                      </strong>
                                    </td>
                                  </tr>
                                </tbody>
                              </Table>
                            </Col>
                          </Row>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
        {renderPagination()}
      </Card>
    </Container>
  );
};

export default StockMonitoring;
